import {
    type ReactNode,
    useEffect
} from "react";

import { useI18n } from "../../i18n/I18nProvider.js";
import type { Language } from "../../types/global.js";
import {
    SpeechProvider,
    useSpeech
} from "../../services/speech/index.js";
import { DinoAssistant } from "./DinoAssistant.js";
import {
    DinoContextProvider,
    useDinoContext,
    type DinoTutorContext
} from "./context/index.js";

interface DinoExperienceProviderProps {
    children: ReactNode;
}

const pageLabels: Record<Language, Record<string, string>> = {
    fr: {
        home: "Accueil",
        grammar: "Grammaire",
        vocabulary: "Vocabulaire",
        travel: "Voyage",
        journal: "Journal",
        practice: "Jeux et exercices",
        daily: "Session du jour",
        profile: "Profil",
        shop: "Boutique",
        admin: "Administration",
        auth: "Compte",
        "not-found": "Page inconnue"
    },
    fa: {
        home: "خانه",
        grammar: "دستور زبان",
        vocabulary: "واژگان",
        travel: "سفر",
        journal: "مجله",
        practice: "بازی‌ها و تمرین‌ها",
        daily: "تمرین امروز",
        profile: "پروفایل",
        shop: "فروشگاه",
        admin: "مدیریت",
        auth: "حساب کاربری",
        "not-found": "صفحه ناشناخته"
    }
};

function getDinoContextLabel(context: Readonly<DinoTutorContext>): string | undefined {
    const language = context.language ?? "fr";
    const kind = context.page?.kind;

    if (!kind) {
        return undefined;
    }

    const label = pageLabels[language][kind] ?? pageLabels[language].home;
    const contentId = context.content?.id;

    return contentId ? `${label} · ${contentId}` : label;
}

function DinoExperienceSurface({ children }: DinoExperienceProviderProps) {
    const { language } = useI18n();
    const context = useDinoContext();
    const {
        stop,
        updateSettings
    } = useSpeech();

    useEffect(
        () => {
            const locale = language === "fa"
                ? "fa-IR"
                : "fr-FR";

            /*
             * Speech always detects the content language. The interface
             * language only supplies a safe fallback when the text is too
             * short or neutral to classify.
             */
            stop();
            updateSettings({
                language: "auto",
                fallbackLocale: locale
            });
        },
        [
            language,
            stop,
            updateSettings
        ]
    );

    return (
        <>
            {children}
            <DinoAssistant contextLabel={getDinoContextLabel(context)} />
        </>
    );
}

function DinoExperienceProvider({ children }: DinoExperienceProviderProps) {
    return (
        <SpeechProvider>
            <DinoContextProvider>
                <DinoExperienceSurface>{children}</DinoExperienceSurface>
            </DinoContextProvider>
        </SpeechProvider>
    );
}

export { DinoExperienceProvider, getDinoContextLabel };
export type { DinoExperienceProviderProps };
